class SCoffeeBuilder extends HTMLElement {
  #abortController;
  #activeCard;
  #announceFrame;

  connectedCallback() {
    const form = this.querySelector('[data-builder-form]');
    if (!form) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    form.addEventListener('click', (event) => this.#handleClick(event), { signal });
    form.addEventListener('change', (event) => this.#handleChange(event), { signal });
    form.addEventListener('submit', (event) => this.#submit(event), { signal });

    this.#cards().forEach((card) => this.#setCardDefault(card));
    const selected = this.querySelector('[data-coffee-trigger][aria-pressed="true"]');
    this.#activeCard = selected?.closest('[data-coffee-card]') || this.#cards()[0] || null;
    if (this.#activeCard) this.#selectCard(this.#activeCard, false);
    this.#syncPurchaseOption();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    cancelAnimationFrame(this.#announceFrame);
  }

  #cards() {
    return Array.from(this.querySelectorAll('[data-coffee-card]'));
  }

  #variants(card) {
    return Array.from(card?.querySelectorAll('[data-variant]') || []).map((node) => ({
      id: node.dataset.id,
      grind: node.dataset.grind,
      size: node.dataset.size,
      price: node.dataset.price,
      available: node.dataset.available === 'true',
      inventoryManaged: node.dataset.inventoryManaged === 'true',
      inventoryPolicy: node.dataset.inventoryPolicy,
      inventoryQuantity: Math.max(0, Number.parseInt(node.dataset.inventoryQuantity || '0', 10) || 0),
      cartQuantity: Math.max(0, Number.parseInt(node.dataset.cartQuantity || '0', 10) || 0),
    }));
  }

  #maxAddable(variant) {
    if (!variant?.available) return 0;
    if (!variant.inventoryManaged || variant.inventoryPolicy === 'continue') return 12;
    return Math.min(12, Math.max(0, variant.inventoryQuantity - variant.cartQuantity));
  }

  #applyVariantToCard(card, variant) {
    if (!card || !variant) return;
    card.dataset.variantId = variant.id;
    card.dataset.grind = variant.grind;
    card.dataset.size = variant.size;
    card.dataset.price = variant.price;
    card.dataset.available = String(this.#maxAddable(variant) > 0);
    card.dataset.inventoryManaged = String(variant.inventoryManaged);
    card.dataset.inventoryPolicy = variant.inventoryPolicy || '';
    card.dataset.inventoryQuantity = String(variant.inventoryQuantity);
    card.dataset.cartQuantity = String(variant.cartQuantity);
  }

  #setCardDefault(card) {
    const variants = this.#variants(card);
    const defaultVariant = variants.find((variant) => variant.grind === 'Standard' && this.#maxAddable(variant) > 0)
      || variants.find((variant) => this.#maxAddable(variant) > 0)
      || variants.find((variant) => variant.grind === 'Standard')
      || variants[0];
    if (!defaultVariant) return;

    this.#applyVariantToCard(card, defaultVariant);
    const max = this.#maxAddable(defaultVariant);
    if (this.#quantity(card) > max) card.dataset.quantity = String(max);
  }

  #handleClick(event) {
    const trigger = event.target.closest('[data-coffee-trigger]');
    if (trigger) {
      const card = trigger.closest('[data-coffee-card]');
      if (card) this.#selectCard(card);
    }
  }

  #handleChange(event) {
    if (event.target.matches('[data-grind-select], [data-size-select]')) {
      this.#saveVariant();
      return;
    }

    if (event.target.matches('[data-quantity-select]')) {
      this.#saveQuantity(event.target.value);
      return;
    }

    if (event.target.matches('[data-purchase-type]')) this.#syncPurchaseOption();
  }

  #selectCard(card, announce = true) {
    this.#cards().forEach((candidate) => {
      const selected = candidate === card;
      const trigger = candidate.querySelector('[data-coffee-trigger]');
      const indicator = candidate.querySelector('[data-selected-indicator]');
      trigger?.setAttribute('aria-pressed', String(selected));
      if (indicator) indicator.hidden = !selected;
    });

    this.#activeCard = card;
    this.#syncPanel();
    const inventoryMessage = this.#inventoryMessage();
    this.#setStatus(inventoryMessage);
    if (announce) this.#announce(inventoryMessage || this.dataset.variantUpdatedMessage);
  }

  #activeVariant(card = this.#activeCard) {
    if (!card) return null;
    return this.#variants(card).find((variant) => variant.id === card.dataset.variantId) || null;
  }

  #syncPanel() {
    if (!this.#activeCard) return;

    const title = this.querySelector('[data-active-title]');
    const price = this.querySelector('[data-active-price]');
    const grind = this.querySelector('[data-grind-select]');
    const size = this.querySelector('[data-size-select]');
    const quantitySelect = this.querySelector('[data-quantity-select]');
    const nativeVariant = this.querySelector('[data-native-variant-id]');
    const nativeQuantity = this.querySelector('[data-native-quantity]');
    const variant = this.#activeVariant();
    const max = this.#maxAddable(variant);
    const quantity = Math.min(this.#quantity(this.#activeCard), max);

    this.#activeCard.dataset.quantity = String(quantity);
    this.#activeCard.dataset.available = String(max > 0);
    if (title) title.textContent = this.#activeCard.dataset.title;
    if (price) price.textContent = this.#activeCard.dataset.price;
    if (grind) grind.value = this.#activeCard.dataset.grind;
    if (size) size.value = this.#activeCard.dataset.size;
    if (quantitySelect) {
      Array.from(quantitySelect.options).forEach((option) => {
        option.disabled = Number(option.value) > max;
      });
      quantitySelect.value = String(quantity);
      quantitySelect.setAttribute('aria-describedby', 'coffee-builder-inventory-status');
    }
    if (nativeVariant) nativeVariant.value = this.#activeCard.dataset.variantId || '';
    if (nativeQuantity) nativeQuantity.value = String(quantity);
    this.#syncSubmitAvailability();
  }

  #inventoryMessage(variant = this.#activeVariant()) {
    const max = this.#maxAddable(variant);
    if (max === 0) return this.dataset.soldOutMessage;
    if (max < 12) return this.dataset.inventoryLimitTemplate.replace('__COUNT__', String(max));
    return '';
  }

  #hasInventoryIssue() {
    return this.#selectedCards().some((card) => {
      const variant = this.#activeVariant(card);
      return !variant || this.#quantity(card) > this.#maxAddable(variant);
    });
  }

  #syncSubmitAvailability() {
    const submit = this.querySelector('[data-add-selected]');
    if (!submit) return;
    submit.disabled = submit.dataset.busy === 'true' || this.#hasInventoryIssue();
  }

  #saveVariant() {
    if (!this.#activeCard) return;

    const grind = this.querySelector('[data-grind-select]')?.value;
    const size = this.querySelector('[data-size-select]')?.value;
    const variants = this.#variants(this.#activeCard);
    const variant = variants.find((item) => item.grind === grind && item.size === size)
      || variants.find((item) => item.grind === grind)
      || variants[0];

    if (variant) {
      this.#applyVariantToCard(this.#activeCard, variant);
    } else {
      this.#activeCard.dataset.grind = grind || '';
      this.#activeCard.dataset.size = size || '';
      this.#activeCard.dataset.variantId = '';
      this.#activeCard.dataset.price = '';
      this.#activeCard.dataset.available = 'false';
    }

    const price = this.querySelector('[data-active-price]');
    if (price) price.textContent = variant?.price || '';

    this.#syncPanel();
    this.#updateCard(this.#activeCard);
    const inventoryMessage = this.#inventoryMessage(variant);
    this.#setStatus(inventoryMessage);
    this.#announce(inventoryMessage || this.dataset.variantUpdatedMessage);
  }

  #quantity(card) {
    const value = Number.parseInt(card?.dataset.quantity || '0', 10);
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }

  #saveQuantity(value) {
    if (!this.#activeCard) return;
    const variant = this.#activeVariant();
    const max = this.#maxAddable(variant);
    const quantity = Math.min(max, Math.max(0, Number.parseInt(value, 10) || 0));
    this.#activeCard.dataset.quantity = String(quantity);
    const quantitySelect = this.querySelector('[data-quantity-select]');
    const nativeQuantity = this.querySelector('[data-native-quantity]');
    if (quantitySelect) quantitySelect.value = String(quantity);
    if (nativeQuantity) nativeQuantity.value = String(quantity);
    this.#updateCard(this.#activeCard);
    this.#syncSubmitAvailability();
    this.#setStatus(max === 0 ? this.dataset.soldOutMessage : '');
  }

  #updateCard(card) {
    const quantity = this.#quantity(card);
    const summary = card.querySelector('[data-card-summary]');

    if (summary) {
      const template = quantity > 0
        ? this.dataset.flavorSelectedTemplate
        : this.dataset.flavorNotSelectedTemplate;
      summary.textContent = template
        .replace('__FLAVOR__', card.dataset.title)
        .replace('__COUNT__', String(quantity));
    }
  }

  #selectedCards() {
    return this.#cards().filter((card) => this.#quantity(card) > 0);
  }

  #sellingPlan(card, name) {
    const data = card.querySelector('[data-selling-plans]');
    if (!data || !name) return null;

    try {
      return JSON.parse(data.textContent).find((plan) => plan.name === name) || null;
    } catch {
      return null;
    }
  }

  #items() {
    const subscription = this.querySelector('[data-purchase-type]:checked')?.value === 'subscription';
    const planName = this.querySelector('[data-plan-name]')?.value;

    return this.#selectedCards().map((card) => {
      if (!card.dataset.variantId) return null;
      const item = {
        id: Number(card.dataset.variantId),
        quantity: this.#quantity(card),
      };

      if (subscription) {
        const plan = this.#sellingPlan(card, planName);
        if (!plan) return null;
        item.selling_plan = Number(plan.id);
      }

      return item;
    }).filter(Boolean);
  }

  #syncPurchaseOption() {
    const subscription = this.querySelector('[data-purchase-type]:checked')?.value === 'subscription';
    const field = this.querySelector('[data-plan-field]');
    if (field) field.hidden = !subscription;
  }

  #formatMoney(cents, currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || this.dataset.currency,
    }).format((Number(cents) || 0) / 100);
  }

  #syncInventoryFromCart(cart) {
    const quantities = new Map();
    if (Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        const id = String(item.variant_id || item.id || '');
        if (id) quantities.set(id, (quantities.get(id) || 0) + (Number(item.quantity) || 0));
      });
    }

    this.#cards().forEach((card) => {
      card.querySelectorAll('[data-variant]').forEach((node) => {
        node.dataset.cartQuantity = String(quantities.get(node.dataset.id) || 0);
      });
      const variant = this.#activeVariant(card);
      if (!variant) return;
      this.#applyVariantToCard(card, variant);
      const max = this.#maxAddable(variant);
      if (this.#quantity(card) > max) card.dataset.quantity = String(max);
      this.#updateCard(card);
    });

    this.#syncPanel();
  }

  async #fetchCart() {
    const url = new URL(this.dataset.cartJsonUrl, window.location.origin);
    url.searchParams.set('_', String(Date.now()));
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(this.dataset.errorMessage);
    return response.json();
  }

  #renderCart(cart) {
    this.#syncInventoryFromCart(cart);
    const count = this.querySelector('[data-cart-count]');
    const list = this.querySelector('[data-cart-list]');
    if (count) {
      const itemCount = Number(cart.item_count) || 0;
      const template = itemCount === 1 ? this.dataset.cartCountOne : this.dataset.cartCountOther;
      count.textContent = template.replace('__COUNT__', String(itemCount));
    }
    if (!list) return;
    list.replaceChildren();

    if (!Array.isArray(cart.items) || cart.items.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'text-sm text-on-surface-secondary';
      empty.textContent = this.dataset.cartEmpty;
      list.appendChild(empty);
      return;
    }

    cart.items.forEach((cartItem) => {
      const item = document.createElement('li');
      item.className = 'flex items-center justify-between gap-4 rounded-input border border-border bg-surface-secondary px-3 py-2';

      const description = document.createElement('div');
      description.className = 'min-w-0';
      const title = document.createElement('p');
      title.className = 'truncate text-sm font-semibold text-on-surface';
      title.textContent = cartItem.product_title;
      description.appendChild(title);

      if (cartItem.variant_title && cartItem.variant_title !== 'Default Title') {
        const options = document.createElement('p');
        options.className = 'mt-0.5 text-xs text-on-surface-secondary';
        options.textContent = cartItem.variant_title;
        description.appendChild(options);
      }

      const details = document.createElement('div');
      details.className = 'shrink-0 text-end';
      const quantity = document.createElement('p');
      quantity.className = 'text-sm font-semibold text-on-surface';
      quantity.textContent = `${this.dataset.quantityLabel} ${cartItem.quantity}`;
      const price = document.createElement('p');
      price.className = 'mt-0.5 text-xs text-on-surface-secondary';
      price.textContent = this.#formatMoney(cartItem.final_line_price, cart.currency);
      details.append(quantity, price);

      item.append(description, details);
      list.appendChild(item);
    });
  }

  #setStatus(message = '') {
    const status = this.querySelector('[data-builder-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  }

  #setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);
    const submit = this.querySelector('[data-add-selected]');
    if (submit) submit.dataset.busy = String(busy);
    this.#syncSubmitAvailability();
  }

  #announce(message) {
    const announcer = document.getElementById('sr-announcer');
    if (!announcer || !message) return;
    cancelAnimationFrame(this.#announceFrame);
    announcer.textContent = '';
    this.#announceFrame = requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  async #submit(event) {
    event.preventDefault();
    this.#setStatus();

    const selected = this.#selectedCards();
    const items = this.#items();
    if (selected.length === 0) {
      this.#setStatus(this.dataset.emptyMessage);
      this.#announce(this.dataset.emptyMessage);
      return;
    }

    const inventoryBlockedCard = selected.find((card) => {
      const variant = this.#activeVariant(card);
      return !variant || this.#quantity(card) > this.#maxAddable(variant);
    });
    if (inventoryBlockedCard) {
      const message = this.#inventoryMessage(this.#activeVariant(inventoryBlockedCard)) || this.dataset.errorMessage;
      this.#setStatus(message);
      this.#announce(message);
      return;
    }

    const subscription = this.querySelector('[data-purchase-type]:checked')?.value === 'subscription';
    if (subscription && items.length !== selected.length) {
      this.#setStatus(this.dataset.subscriptionUnavailableMessage);
      this.#announce(this.dataset.subscriptionUnavailableMessage);
      return;
    }

    if (items.length !== selected.length) {
      this.#setStatus(this.dataset.errorMessage);
      this.#announce(this.dataset.errorMessage);
      return;
    }

    this.#setBusy(true);
    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        let message = this.dataset.errorMessage;
        try {
          const error = await response.json();
          message = error.description || error.message || message;
        } catch {
          // Keep the translated fallback when Shopify does not return JSON.
        }
        throw new Error(message);
      }
    } catch (error) {
      const message = error.message || this.dataset.errorMessage;
      this.#setStatus(message);
      this.#announce(message);
      this.#setBusy(false);
      return;
    }

    this.#announce(this.dataset.addedMessage);

    try {
      const cart = await this.#fetchCart();
      this.#renderCart(cart);

      // Refresh the header and the drawer's stored markup without opening or navigating to either cart surface.
      await customElements.whenDefined('s-cart');
      await customElements.get('s-cart')?.refreshMountedCarts?.();
    } catch {
      // The add succeeded, so a follow-up display refresh must not report a cart failure.
    } finally {
      this.#setBusy(false);
    }
  }
}

if (!customElements.get('s-coffee-builder-v7')) {
  customElements.define('s-coffee-builder-v7', SCoffeeBuilder);
}
